// This ESLint rule is disabled because this file makes use of some "magic" numbers for determining terminal orientations.
/* eslint-disable @typescript-eslint/no-magic-numbers */
import { defaultPropertyDefinitions, getDesignatorPrefixForPartType, partPropertyCategories, } from "@buildwithflux/constants";
import { nestedNodeDelimiter, constructNodeUidForSubLayout, directionOrientation, elementMatchesPartType, getElementPartCategoryProperty, matchesPartType, invertDirection, isGroundElement, Orientation, PcbNodeTypes, GENESIS_TERMINAL_PART_UID, ROUTE_BRANCH_POINT_PART_UID, } from "@buildwithflux/models";
import { brandedKeys, brandedValues, guid, keySort, naturalSort, naturalSortByPropertyWithFallbacks, orientationToSteps, Records, } from "@buildwithflux/shared";
import { chain, cloneDeep, groupBy, mapValues, uniq } from "lodash";
import { LRUCache } from "lru-cache";
import { box2, vec2 } from "../common";
import { deprecatedPartCategoryPropertyKey, getDefaultValueProperty, getDesignatorPrefixFromProperties, getIsExcludedFromBOM, getProperty, getPropertyValue, portalPartType, PropertyTypes, } from "../property";
import { SymbolLayout } from "./SymbolLayout";
// We want a name other than "" for the default symbol (partly because you can't save
// "" as a key to firestore, partly because "" is just a weird symbol name. This doesn't
// affect the displayed name)
export const DEFAULT_SYMBOL_NAME = "MAIN";
const defaultPinNameOptions = {
    withElementDesignatorPrefix: true,
    delimiter: ":",
};
const { HORIZONTAL } = Orientation;
// Allows `max` *unique* components before cache is no longer useful. When eg wiring, this
// is the number of unique components on the schematic canvas.
// The max of 1k was loosely chosen to accommodate our biggest 10k component goal, assuming
// that in a project that large, most of those components would be duplicates or contained
// in modules, so 1/10th the max number for this cache should provide plenty headroom.
// [workplace-post/459034239506853](/docs/workplace/459034239506853.md#459084366168507)
//
// Note: it might be tempting to key this cache by part version uid and avoid an O(N)-expensive
// cacheKey, since terminals would usually only change IFF part version changes. But that's
// probably a bad idea at this level, because sortTerminals() is not a pure function of partVersion
// UID, so using that as the key would create opportunities for cache bugs. Things like usercode
// can theoretically affect terminal order. Also, it would mean sortTerminals requires a part version,
// whereas in some cases we might want to run it on an unpublished document's terminals.
// A temporary by-part cache is provided below, to optimise tight loops where we can be sure
// the data is stable.
const sortedTerminalsCache = new LRUCache({ max: 1000 });
function isTerminal(element) {
    return elementMatchesPartType(element, "Terminal", { includeParents: false });
}
function hasTerminals(element) {
    const terminalsMap = element.part_version_data_cache?.terminals;
    const terminalsSpecifiedInTerminalsMap = !!terminalsMap && Object.values(terminalsMap).length > 0;
    if (terminalsSpecifiedInTerminalsMap) {
        return true;
    }
    const subElements = element.part_version_data_cache?.extracted_element_cache;
    if (!subElements)
        return false;
    const hasImmediateChildTerminal = !!Records.findValue(subElements, isTerminal);
    return hasImmediateChildTerminal;
}
function isLayout(node) {
    return node.type === PcbNodeTypes.layout;
}
function containsLayout(nodes) {
    return Object.values(nodes).some((n) => isLayout(n));
}
/**
 * Determine whether an element should have a footprint (e.g. a resistor or an IC), as opposed to a layout.
 *
 * @remarks The element itself may not have a rendered footprint node in all contexts, e.g. if imported into another
 * document. Regardless, it should always have a footprint when opened as a standalone project.
 *
 * @see getFootprints
 */
export function shouldHaveFootprint(element) {
    const layoutNodes = element.part_version_data_cache?.pcbLayoutNodes;
    if (layoutNodes && containsLayout(layoutNodes)) {
        return false;
    }
    if (!hasTerminals(element)) {
        return false;
    }
    const subElements = element.part_version_data_cache?.extracted_element_cache;
    const noSubElementHasFootprint = !subElements || !Object.values(subElements).some(shouldHaveFootprint);
    return noSubElementHasFootprint;
}
// TODO: Need test coverage for these helpers
export class ElementHelper {
    static createNewElementData(partVersionData, position, userUid, designator, elementUid) {
        const clonedPartVersion = cloneDeep(partVersionData);
        const positionIsMultiple = typeof position.x !== "number";
        const elementData = {
            uid: (elementUid || guid()),
            label: designator,
            created_at: new Date().getTime(),
            updated_at: new Date().getTime(),
            owner_uid: userUid,
            part_uid: partVersionData.part_uid,
            part_version: partVersionData.version,
            part_version_data_cache: clonedPartVersion,
            // Only preserved so we don't break legacy projects for prod/release while multisymbols is in main:
            diagram_position: positionIsMultiple ? Object.values(position)[0] : position,
            symbol_placements: {},
            properties: cloneDeep(clonedPartVersion.properties),
        };
        if (positionIsMultiple) {
            elementData.symbol_placements = position;
        }
        else {
            let positions = ElementHelper.calculateSymbolPlacements(elementData, position, {});
            // If the caller specifies a single position, make sure we copy any flip/orientation flags into
            // symbol_placements. Note: this is mainly for single-symbol elements. It doesn't make much sense
            // for a caller to specify a single flip/orientation for multiple symbols.
            positions = mapValues(positions, (pos) => ({ ...position, ...pos }));
            elementData.symbol_placements = positions;
        }
        return elementData;
    }
    /**
     * Returns a new designator sequencer based off of input element map. The sequencer allows calling
     * code to obtain a sequentially unique designator (with respect to input element map), that's unique
     * to the designator prefix obtained from the version data of the specified part
     *
     * @param elementMap
     * @returns
     */
    static createDesignatorSequencer(elementMap) {
        // Local resuable helper used to ensure reliable string matching
        const homogenizeString = (value) => value.trim().toLowerCase();
        // Obtain a flat list of unique designators from the input element map
        const designatorSet = new Set(ElementHelper.getAllElementsRecursively(elementMap)
            .filter((element) => element.label)
            .map((element) => homogenizeString(element.label ?? "")));
        function buildElementDesignatorString(designatorPrefix, designatorCount) {
            return `${designatorPrefix}${designatorCount}`;
        }
        // Define the external API that allows calling code to obtain next
        // ordinal/sequential designator with respect to specified element
        // map
        function findNextDesignatorFromPartVersionData(partVersionData) {
            // First, try to get the designator prefix from the part's properties
            let designatorPrefix = getDesignatorPrefixFromProperties(partVersionData.properties);
            // If no designator prefix found, check the part type
            if (!designatorPrefix) {
                const partType = getPropertyValue(defaultPropertyDefinitions.part_type.label, partVersionData.properties);
                if (partType && typeof partType === "string") {
                    // Ground elements should not have designators - return empty string explicitly
                    if (matchesPartType(partType, "Ground")) {
                        return "";
                    }
                    designatorPrefix = getDesignatorPrefixForPartType(partType);
                }
            }
            // If still no designator prefix, use "U" as the final fallback
            if (!designatorPrefix) {
                designatorPrefix = "U";
            }
            // Perform linear search to find next unique designator
            for (let count = 1;; count++) {
                const designator = buildElementDesignatorString(designatorPrefix, count);
                if (!designatorSet.has(homogenizeString(designator))) {
                    // A unique designator found, update the local set
                    // of designators and return current unique designator
                    // to caller
                    designatorSet.add(homogenizeString(designator));
                    return designator;
                }
            }
        }
        function findNextDesignatorFromElementData(elementData) {
            return findNextDesignatorFromPartVersionData(elementData.part_version_data_cache);
        }
        /**
         * Checks if a designator is unique (not already in use).
         * If unique, reserves it by adding to the internal set and returns true.
         * If not unique, returns false.
         */
        function tryReserveDesignator(designator) {
            const normalizedDesignator = homogenizeString(designator);
            if (designatorSet.has(normalizedDesignator)) {
                return false;
            }
            designatorSet.add(normalizedDesignator);
            return true;
        }
        return {
            findNextDesignatorFromPartVersionData,
            findNextDesignatorFromElementData,
            tryReserveDesignator,
        };
    }
    static getTerminalWithProperties(element, terminalUid) {
        const partVersionData = element.part_version_data_cache;
        const terminalData = partVersionData.terminals[terminalUid];
        if (!terminalData)
            return undefined;
        const terminal = { ...terminalData };
        const completeProperties = (partVersionData.extracted_element_cache?.[terminalData.uid]?.properties ??
            {});
        terminal.properties = { ...completeProperties, ...(terminalData.properties ?? {}) };
        return terminal;
    }
    /**
     * Set the value of the pin number property.  If there is not already a pin number property, one will be created.
     */
    static setPinNumberProperty(element, value) {
        const existingProperty = getProperty(defaultPropertyDefinitions.pin_number.label, element.properties);
        if (existingProperty != null) {
            const updatedProperty = {
                ...existingProperty,
                value,
            };
            return {
                ...element,
                properties: {
                    ...element.properties,
                    [updatedProperty.uid]: updatedProperty,
                },
            };
        }
        const newProperty = {
            uid: guid(),
            value,
            name: defaultPropertyDefinitions.pin_number.label,
            key: defaultPropertyDefinitions.pin_number.key,
        };
        return {
            ...element,
            properties: {
                ...element.properties,
                [newProperty.uid]: newProperty,
            },
        };
    }
    static setPartTypeProperty(element, value) {
        const existingProperty = getProperty(defaultPropertyDefinitions.part_type.label, element.properties);
        if (existingProperty != null) {
            const updatedProperty = {
                ...existingProperty,
                value,
            };
            return {
                ...element,
                properties: {
                    ...element.properties,
                    [updatedProperty.uid]: updatedProperty,
                },
            };
        }
        const newProperty = {
            uid: guid(),
            value,
            name: defaultPropertyDefinitions.part_type.label,
            key: defaultPropertyDefinitions.part_type.key,
        };
        return {
            ...element,
            properties: {
                ...element.properties,
                [newProperty.uid]: newProperty,
            },
        };
    }
    static getTerminals(element, withAllProperties = false) {
        return Object.values(element.part_version_data_cache.terminals).map((terminal) => {
            return withAllProperties ? this.getTerminalWithProperties(element, terminal.uid) : terminal;
        });
    }
    static getTerminalsBySymbolName(element, withAllProperties = false) {
        const terminals = this.getTerminals(element, withAllProperties);
        return groupBy(terminals, (t) => {
            const symbolName = t.properties?.[defaultPropertyDefinitions.subsymbol_designator_suffix.key]?.value;
            return symbolName ?? DEFAULT_SYMBOL_NAME;
        });
    }
    /** Return terminals sorted as per .sortTerminals() below. */
    static getSortedTerminals(element, withAllProperties = false) {
        return this.sortTerminals(this.getTerminals(element, withAllProperties));
    }
    /** Return terminals sorted by type, and order|name|uid within each type group. */
    static sortTerminals(terminals) {
        // Note: the perf of this cachekey depends on `terminals` being in a stable order between
        // calls (but not necessarily in the final sorted order). This is generally true for
        // our callpaths because `terminals` is usually referentially the same object for the same
        // element, and also the process that generates it is usually stable.
        const cacheKey = JSON.stringify(terminals.map((t) => [t.type, t.order, t.name, t.uid]));
        if (!sortedTerminalsCache.has(cacheKey)) {
            const terminalTypes = uniq(terminals.map((t) => t.type).filter((t) => !!t));
            terminalTypes.sort(naturalSort({ deterministic: true }));
            terminalTypes.push("");
            // Map of indices to avoid an O(n*m) loop further down.
            const terminalTypeIndices = Object.fromEntries(terminalTypes.map((t, idx) => [[t], idx]));
            // JS sort is stable (nowadays), so the following two calls will result in an array sorted by
            // (type, order|name|uid).
            // TODO the `order|name|uid` sort here is NOT a column sort, eg (order, name, uid) -- it's
            // a fallback sort (eg [].sortBy(order || name), vs. [].sortBy(name).sortBy(order)). The
            // implication of this is that it won't actually sort stably if two terminals have the
            // same order or the same name. It works in 99% of cases by ~accident because fallback
            // sort falls back if the field is an empty string, and so an unset terminal order will
            // result in sorting by name. This isn't great, though!
            terminals.sort(naturalSortByPropertyWithFallbacks("order", "name", "uid", { deterministic: true }));
            terminals.sort(keySort((t) => terminalTypeIndices[t.type ?? ""]));
            sortedTerminalsCache.set(cacheKey, terminals.map((t) => t.uid));
        }
        const terminalsById = Object.fromEntries(terminals.map((t) => [t.uid, t]));
        return sortedTerminalsCache.get(cacheKey).map((uid) => terminalsById[uid]);
    }
    static isPortal(element) {
        return getElementPartCategoryProperty(element)?.value === portalPartType;
    }
    static isPortalOrGroundElement(element) {
        return ElementHelper.isPortal(element) || isGroundElement(element);
    }
    static isExcludedFromPcb(properties) {
        const excludeInPcbProp = Records.findValue(properties, (prop) => prop.name === defaultPropertyDefinitions.exclude_from_pcb.label);
        if (!excludeInPcbProp) {
            return undefined;
        }
        const value = excludeInPcbProp.value.toString();
        return value?.toLowerCase() === "true" || value === "1";
    }
    static hasCustomSymbol(element) {
        return Boolean(element.part_version_data_cache.symbol_resource_file);
    }
    // eslint-disable-next-line @typescript-eslint/member-ordering
    static defaultPropertiesByLabel = chain(defaultPropertyDefinitions)
        .filter((value) => value.type === PropertyTypes.value)
        .map((value) => [value.label, value])
        .fromPairs()
        .value();
    static extractElementDataWithoutExpressionEvaluation(elementData) {
        // Default properties to an empty object
        const properties = elementData.properties ?? {};
        // Extract element data needed for sub layouts to work.
        const defaultProperty = getDefaultValueProperty(properties);
        return {
            label: elementData.label,
            defaultPropertyValue: defaultProperty?.value.toString() || undefined,
            properties: this.extractPropertiesWithoutEvaluation(properties),
            document_import_uid: elementData.part_version_data_cache.document_import_uid,
            part_version_data_cache: {
                name: elementData.part_version_data_cache.name,
                extracted_element_cache: elementData.part_version_data_cache.extracted_element_cache,
                pcbLayoutRuleSets: elementData.part_version_data_cache.pcbLayoutRuleSets,
                part_uid: elementData.part_version_data_cache.part_uid,
                assets: elementData.part_version_data_cache.assets,
            },
        };
    }
    static extractPropertiesWithoutEvaluation(properties) {
        return Object.values(properties)
            .filter((v) => v)
            .reduce((accumulator, propertyData) => {
            return {
                ...accumulator,
                [propertyData.uid]: {
                    name: propertyData.name,
                    value: propertyData.value,
                },
            };
        }, {});
    }
    static getPinNumberProperty(element) {
        return getProperty(defaultPropertyDefinitions.pin_number.label, element.properties);
    }
    static isTerminalElement(element) {
        return elementMatchesPartType(element, partPropertyCategories.terminal, { includeParents: false });
    }
    static getElementPartCategoryProperty(element) {
        const properties = element.properties ?? {};
        const propArray = Object.values(properties);
        return (propArray.find((prop) => prop.name === defaultPropertyDefinitions.part_type.label) ||
            properties[deprecatedPartCategoryPropertyKey]);
    }
    static setPinSubSymbolDesignatorProperty(element, value) {
        const existingProperty = getProperty(defaultPropertyDefinitions.subsymbol_designator_suffix.label, element.properties);
        if (existingProperty != null) {
            const updatedProperty = {
                ...existingProperty,
                value,
            };
            return {
                ...element,
                properties: {
                    ...element.properties,
                    [updatedProperty.uid]: updatedProperty,
                },
            };
        }
        const newProperty = {
            uid: defaultPropertyDefinitions.subsymbol_designator_suffix.key,
            value,
            name: defaultPropertyDefinitions.subsymbol_designator_suffix.label,
            key: defaultPropertyDefinitions.subsymbol_designator_suffix.key,
        };
        return {
            ...element,
            properties: {
                ...element.properties,
                [newProperty.uid]: newProperty,
            },
        };
    }
    /**
     * @deprecated Use `isGroundElement()` instead (non-method)
     */
    static isGroundElement(element) {
        return isGroundElement(element);
    }
    static isGenesisTerminalPartVersion(partUid) {
        return GENESIS_TERMINAL_PART_UID === partUid;
    }
    static isBranchPointElement(element) {
        return element.part_version_data_cache?.part_uid
            ? ROUTE_BRANCH_POINT_PART_UID === element.part_version_data_cache.part_uid
            : ElementHelper.isExtractedElement(element) && element.partName === "RouteBranchPoint";
    }
    static getSymbolPinPositionProperty(element) {
        return getProperty(defaultPropertyDefinitions.symbol_pin_position.label, element.properties);
    }
    static isValidLayoutElement(element, documentUid) {
        return (element.part_version_data_cache.pcbLayoutNodes &&
            !ElementHelper.isTerminalElement(element) &&
            !ElementHelper.isGroundElement(element) &&
            !ElementHelper.isExcludedFromPcb(element.properties) &&
            !ElementHelper.isBranchPointElement(element) &&
            element.part_version_data_cache.document_import_uid !== documentUid);
    }
    static isValidGenesisTerminalElement(element) {
        return (element.part_version_data_cache.pcbLayoutNodes &&
            ElementHelper.isTerminalElement(element) &&
            !ElementHelper.isExcludedFromPcb(element.properties));
    }
    /** Determine whether the provided element is a top-level element, or taken from a nested
     * element inside extracted_element_cache.
     * Note, the method we use isn't a very well-defined way of determining whether an element
     * is extracted or not; however, given the current data model, it's the most relevant signal
     * we have. If in future we also extract uid, we'll have to provide a more robust method
     * (or, better, make sure that the right fields are extracted so code that works with
     * both types doesn't need to distinguish).
     */
    static isExtractedElement(element) {
        return !("uid" in element);
    }
    static makeSymbolUid(elementUid, symbolName) {
        return `symbol:${elementUid}:${symbolName}`;
    }
    static parseSymbolUid(symbolUid) {
        const match = symbolUid.match(/^symbol:([^:]*):(.*)$/);
        if (!match)
            return undefined;
        return { elementUid: match[1], symbolName: match[2] };
    }
    static calculateSymbolPlacements(element, startPosition = { x: 0, y: 0 }, assignedPositions = {}, derivedSymbolInfo) {
        derivedSymbolInfo = derivedSymbolInfo ?? SymbolLayout.getSymbolInfo(element);
        let positionOffset = derivedSymbolInfo[0].outerBounds.min.x - derivedSymbolInfo[0].pinLength;
        const symbolPositions = {};
        for (const symbol of derivedSymbolInfo) {
            const size = box2.size(symbol.outerBounds);
            const assignedPosition = assignedPositions[symbol.name] ??
                vec2.add(startPosition, { x: positionOffset + size.x / 2 + symbol.pinLength, y: 0 });
            symbolPositions[symbol.name] = assignedPosition;
            // Leave space for both symbols' pin labels between the pins, and a bit of breathing room.
            // Because labels can be arbitrarily long, we just assume that labels usually won't be
            // longer than twice the width of a pin.
            const labelBreathingSpace = symbol.pinLength * 4 + 20;
            positionOffset += size.x + labelBreathingSpace;
        }
        return symbolPositions;
    }
    static getSymbolPlacements(element, derivedSymbolInfo) {
        if (element.symbol_placements) {
            // We backfill any missing symbol positions below, to ensure things don't break if a
            // part is updated to have more/different symbols.
            return ElementHelper.calculateSymbolPlacements(element, { x: 0, y: 0 }, element.symbol_placements, derivedSymbolInfo);
        }
        else {
            derivedSymbolInfo = derivedSymbolInfo ?? SymbolLayout.getSymbolInfo(element);
            if (derivedSymbolInfo.length === 1) {
                // Special case to handle old-style diagram_position when there is only one symbol,
                // to preserve .flip and .orientation, and avoid fragile fallbacks in the loop below.
                return { [derivedSymbolInfo[0].name]: element.diagram_position ?? { x: 0, y: 0 } };
            }
            return ElementHelper.calculateSymbolPlacements(element, element.diagram_position, {}, derivedSymbolInfo);
        }
    }
    static setSymbolPlacement(partElement, partialSchematicPosition2, placementKey = DEFAULT_SYMBOL_NAME) {
        const placements = ElementHelper.getSymbolPlacements(partElement);
        const placementByKey = placements[placementKey];
        if (placementByKey) {
            placements[placementKey] = {
                ...placementByKey,
                ...partialSchematicPosition2,
            };
            partElement.symbol_placements = placements;
        }
    }
    static getSymbolStyle(element) {
        const propertyValueRaw = getPropertyValue(defaultPropertyDefinitions.symbol_style.label, element.properties);
        const propertyToLayoutStrategyMap = {
            Legacy: "legacy",
            Parametric: "parametric-v0",
        };
        const propertyValue = propertyToLayoutStrategyMap[propertyValueRaw];
        return propertyValue ?? element.part_version_data_cache.symbolLayoutStrategy ?? "legacy";
    }
    /** Get the canvas position and orientation of an element's terminals. */
    static getAbsoluteTerminalPlacements(element, derivedSymbolInfo) {
        const symbolInfo = derivedSymbolInfo ?? SymbolLayout.getSymbolInfo(element);
        const placements = {};
        const symbolPositions = ElementHelper.getSymbolPlacements(element, symbolInfo);
        for (const symbol of symbolInfo) {
            const symbolPos = symbolPositions[symbol.name];
            if (!symbolPos)
                continue;
            for (const [pinUid, pinLocation] of Object.entries(symbol.pinLocations)) {
                placements[pinUid] = {
                    position: ElementHelper.relativePosToAbsolute(pinLocation.position, { reference: symbolPos }),
                    direction: ElementHelper.relativeDirectionToAbsolute(pinLocation.direction, { reference: symbolPos }),
                };
            }
        }
        return placements;
    }
    /**
     * Gets a list of all pins that are connected to the given pin via a wire.  Does not include the originating pin.
     * It also does not include transitive connections.  For example, if A is wired to B, and B is wired to C,
     * and this function is called with respect to a pin belonging to A, the result will include B but not C.
     *
     * TODO: Should this function throw if the given pin does not exist?
     */
    static getDirectlyConnectedPins(pin, document) {
        // The strategy is to start at the route vertice(s) connected to the terminal directly and then search from there.
        const searchRoots = brandedValues(document.routeVertices).filter((rv) => {
            return rv.terminal?.element_uid === pin.element_uid && rv.terminal?.terminal_uid === pin.terminal_uid;
        });
        if (searchRoots.length === 0) {
            return [];
        }
        // Now we do a BFS on the route vertices to find all connected elements.
        let current = searchRoots.pop();
        const visited = new Set();
        const discoveredElementUids = new Set();
        while (current != null) {
            if (visited.has(current.id)) {
                current = searchRoots.pop();
                continue;
            }
            if (current.terminal &&
                current.terminal.element_uid !== pin.element_uid &&
                current.terminal.terminal_uid !== pin.terminal_uid) {
                discoveredElementUids.add(current.terminal);
                for (const potentialSearchRoot of Object.values(document.routeVertices)) {
                    if (potentialSearchRoot.terminal === current.terminal) {
                        searchRoots.push(potentialSearchRoot);
                    }
                }
            }
            for (const connectedVertexId of brandedKeys(current.connectedVertices)) {
                const connectedVertex = document.routeVertices[connectedVertexId];
                if (connectedVertex == null) {
                    // What are we supposed to do here?  This is an integrity violation, but for our purposes,
                    // clearly the route vertex is not connected to anything _meaningful_, so we just ignore it.
                }
                else {
                    searchRoots.push(connectedVertex);
                }
            }
            visited.add(current.id);
            current = searchRoots.pop();
        }
        return Array.from(discoveredElementUids);
    }
    static relativeDirectionToAbsolute(direction, { reference }) {
        // Rotate/flip the directions to put them into canvas-space:
        if (reference.orientation) {
            const rotationSteps = orientationToSteps(reference.orientation);
            const directions = ["left", "down", "right", "up"];
            direction = directions[(directions.indexOf(direction) + rotationSteps) % 4];
        }
        if (reference.flip) {
            direction = directionOrientation(direction) === HORIZONTAL ? invertDirection(direction) : direction;
        }
        return direction;
    }
    static relativeBoundsToAbsolute(bounds, { reference }) {
        return box2.fromPoints([
            ElementHelper.relativePosToAbsolute(bounds.min, { reference }),
            ElementHelper.relativePosToAbsolute(bounds.max, { reference }),
        ]);
    }
    static absoluteBoundsToRelative(bounds, { reference }) {
        return box2.fromPoints([
            ElementHelper.absolutePosToRelative(bounds.min, { reference }),
            ElementHelper.absolutePosToRelative(bounds.max, { reference }),
        ]);
    }
    /** Transform a local-to-symbol point to canvas space.
     *
     * Note: DO NOT CHANGE THE `{reference}` parameter to a bare parameter. Because when this
     * function has two position parameters, it is easy for the caller to unwittingly swap them in a way that
     * TypeScript won't catch, and this results in non-obvious bugs that are only visible when eg hovering
     * rotated/flipped element terminals. So leave the parameter as `{reference}` so TypeScript forces the
     * developer to get the correct position param in the right place.
     */
    static absolutePosToRelative(pos, { reference }) {
        pos = {
            x: pos.x - reference.x,
            y: pos.y - reference.y,
        };
        if (reference.flip) {
            pos.x = -pos.x;
        }
        return vec2.rotate(pos, -(reference.orientation ?? 0));
    }
    /** Transform a canvas-space point to the symbol's local space.
     *
     * Note: DO NOT CHANGE THE `{reference}` parameter to a bare parameter. See above.
     */
    static relativePosToAbsolute(pos, { reference }) {
        pos = vec2.rotate(pos, reference.orientation ?? 0);
        if (reference.flip) {
            pos.x = -pos.x;
        }
        return {
            x: pos.x + reference.x,
            y: pos.y + reference.y,
        };
    }
    static getAllElementsRecursively(elements) {
        elements = Array.isArray(elements) ? Object.fromEntries(elements.map((elm) => [elm.uid, elm])) : elements;
        let allElements = [];
        for (const [uid, element] of Object.entries(elements)) {
            const subElements = ElementHelper.getAllElementsRecursively(element.part_version_data_cache?.extracted_element_cache ?? {});
            allElements = allElements.concat(subElements);
            allElements.push({ uid, ...element });
        }
        return allElements;
    }
    static flattenElements(elements) {
        const flat = {};
        for (const [uid, element] of Object.entries(elements)) {
            flat[uid] = element;
            const subElements = ElementHelper.flattenElements(element.part_version_data_cache?.extracted_element_cache ?? {});
            for (const [subUid, subElement] of Object.entries(subElements)) {
                const nestedUid = constructNodeUidForSubLayout(uid, subUid);
                flat[nestedUid] = subElement;
            }
        }
        return flat;
    }
    /**
     * Goes thru all part_version_data_cache to get the element data for a node
     * that may be in a sub-layout.
     */
    static getNestedElementData(elements, nodeUid) {
        const uids = nodeUid.split(nestedNodeDelimiter);
        let elementsMap = elements;
        let elementData;
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        uids.forEach((uid) => {
            if (elementsMap && elementsMap[uid]) {
                elementData = elementsMap[uid];
            }
            elementsMap = elementData?.part_version_data_cache?.extracted_element_cache;
        });
        return elementData;
    }
    static isExcludedFromBOM(element) {
        const isAlwaysExcludedFromBOM = ElementHelper.isTerminalElement(element) ||
            isGroundElement(element) ||
            ElementHelper.isBranchPointElement(element);
        if (isAlwaysExcludedFromBOM)
            return true;
        const hasExcludedFromBomPropertySet = element.properties && getIsExcludedFromBOM(element.properties);
        if (hasExcludedFromBomPropertySet)
            return true;
        return !shouldHaveFootprint(element);
    }
    static isValidBomElement(element) {
        return !ElementHelper.isExcludedFromBOM(element);
    }
    static getPinName(element, terminalOrPin, options = defaultPinNameOptions) {
        const withElementDesignatorPrefix = options.withElementDesignatorPrefix ?? defaultPinNameOptions.withElementDesignatorPrefix;
        const terminalData = "terminalUid" in terminalOrPin
            ? element.part_version_data_cache?.terminals?.[terminalOrPin.terminalUid]
            : terminalOrPin;
        if (!terminalData) {
            return undefined;
        }
        const elementDesignator = element.label?.trim();
        if (!elementDesignator && withElementDesignatorPrefix) {
            return undefined;
        }
        const terminalLabel = terminalData.name?.trim();
        const terminalPinNumber = terminalData.pin_number?.toString().trim();
        const terminalName = terminalLabel || terminalPinNumber;
        let pinName = "";
        if (withElementDesignatorPrefix) {
            pinName += `${elementDesignator}${options.delimiter ?? defaultPinNameOptions.delimiter}`;
        }
        pinName += terminalName;
        if (pinName) {
            // If the pin name is not empty, return it.
            return pinName;
        }
        return undefined;
    }
}
//# sourceMappingURL=ElementHelper.js.map